import { Typography, makeStyles } from '@material-ui/core';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as Error } from 'assets/general/icons/Error.svg';
import inProgressIcon from 'assets/general/icons/inProgress.svg';
import timerIcon from 'assets/stripeVerification/timerIcon.svg';
import useKycStatus from 'hooks/useKycStatus';
import useProfile from 'hooks/useProfile';
import { KYCStatus } from 'services/types/kycStatus';
import { openIdentityVerificationDialog } from 'store/actions/identityVerificationActions';
import {
  getIdentityVerificationAutoOpenDismissedKey,
  getIdentityVerificationStatusKey,
  readIdentityVerificationAutoOpenDismissed,
} from 'services/helpers/identityVerification';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    backgroundColor: '#FEDACD',
    height: 55,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(0, 3),
    boxSizing: 'border-box',
    cursor: 'pointer',
    gap: '24px',
  },
  message: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  icon: {
    flexShrink: 0,
  },
  timer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    whiteSpace: 'nowrap',
  },
  timerIcon: {
    width: 20,
    height: 20,
    flexShrink: 0,
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    height: 24,
    padding: '2px 8px',
    borderRadius: 8,
    backgroundColor: '#F4BDA9',
    boxSizing: 'border-box',
  },
  badgeIcon: {
    width: 16,
    height: 16,
    flexShrink: 0,
  },
  badgeText: {
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
}));

const padTime = (value: number) => String(value).padStart(2, '0');

const formatTimeLeft = (expiresAt: number | null) => {
  if (!expiresAt) return '--:--';

  const diff = expiresAt - Date.now();

  if (diff <= 0) {
    return null;
  }

  const totalSeconds = Math.floor(diff / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${padTime(hours)}:${padTime(minutes)}:${padTime(seconds)}`;
};

const IdentityVerificationBanner = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { profile } = useProfile();
  const { open, dismissedKey, verificationInProgress } = useSelector(
    (state: any) => state.identityVerification || {}
  );

  const stripeVerificationStatus = profile?.stripeVerificationStatus as string | undefined;
  const { secondsLeft, status, hasCountdown, isVerificationRequired } = useKycStatus(
    stripeVerificationStatus,
    Boolean(verificationInProgress)
  );
  const shouldShowBanner = useMemo(
    () => isVerificationRequired && hasCountdown,
    [hasCountdown, isVerificationRequired]
  );

  const expiresAt = useMemo(() => {
    if (secondsLeft === undefined || secondsLeft === null) return null;

    const parsed = Number(secondsLeft);

    return Number.isNaN(parsed) ? null : Date.now() + parsed * 1000;
  }, [secondsLeft]);

  const [timeLeft, setTimeLeft] = useState<string | null>(formatTimeLeft(expiresAt));
  const loginKey = useMemo(() => {
    if (!profile?.sub) return null;

    const authTime = (profile as { auth_time?: number; iat?: number }).auth_time;
    const issuedAt = (profile as { iat?: number }).iat;
    const loginStamp = authTime ?? issuedAt ?? 'na';

    return `${profile.sub}-${loginStamp}`;
  }, [profile]);
  const autoOpenDismissedKey = useMemo(
    () => getIdentityVerificationAutoOpenDismissedKey(loginKey),
    [loginKey]
  );
  const statusKey = useMemo(() => {
    return getIdentityVerificationStatusKey(status, secondsLeft, stripeVerificationStatus);
  }, [secondsLeft, status, stripeVerificationStatus]);

  useEffect(() => {
    if (!shouldShowBanner) {
      setTimeLeft(null);
      return;
    }

    const updateTimeLeft = () => {
      setTimeLeft(formatTimeLeft(expiresAt));
    };

    updateTimeLeft();

    if (!expiresAt || status === KYCStatus.STARTED) return;

    const intervalId = setInterval(updateTimeLeft, 1000);

    return () => clearInterval(intervalId);
  }, [expiresAt, shouldShowBanner, status]);

  // Assumption: API-backed status is authoritative for determining "in progress" display.
  const isKycStarted = status === KYCStatus.STARTED;

  useEffect(() => {
    const hasDismissedAutoOpen = readIdentityVerificationAutoOpenDismissed(autoOpenDismissedKey);

    if (
      shouldShowBanner &&
      !open &&
      !verificationInProgress &&
      statusKey &&
      dismissedKey !== statusKey &&
      !hasDismissedAutoOpen
    ) {
      dispatch(openIdentityVerificationDialog({ variant: 'required' }));
    }
  }, [
    autoOpenDismissedKey,
    dismissedKey,
    dispatch,
    open,
    shouldShowBanner,
    statusKey,
    verificationInProgress,
  ]);

  const handleClickBanner = () => {
    if (verificationInProgress) return;
    dispatch(openIdentityVerificationDialog({ variant: 'required' }));
  };

  if (!shouldShowBanner) return null;

  return (
    <div className={classes.root} onClick={handleClickBanner}>
      <div className={classes.message}>
        <Error className={classes.icon} />
        <Typography>
          Identity verification is required to continue using all app features without
          interruptions.
        </Typography>
      </div>
      {isKycStarted ? (
        <div className={classes.timer}>
          <div className={classes.badge}>
            <img src={inProgressIcon} alt="" className={classes.badgeIcon} />
            <Typography variant="body2" className={classes.badgeText}>
              In progress
            </Typography>
          </div>
        </div>
      ) : (
        timeLeft && (
          <div className={classes.timer}>
            <img src={timerIcon} alt="" className={classes.timerIcon} />
            <Typography>{`Time left: ${timeLeft}`}</Typography>
          </div>
        )
      )}
    </div>
  );
};

export default IdentityVerificationBanner;
